import { useCallback, useEffect, useState } from 'react';
import type { Profesional } from '../../api/ops/citas';
import type { ReglaReserva } from '../../api/ops/disponibilidad';
import type { Politica } from '../../api/ops/pagos';
import type { PlantillaDocumento, ServicioCatalogo } from '../../api/ops/servicios';
import type { Operaciones } from '../../api/operaciones';
import { todas } from '../agenda/logica';

/**
 * Lo que lee «Servicios» (9.4): el catálogo entero (activos y archivados), los profesionales (quién lo hace), y lo que se
 * ve en la ficha de otros módulos según los permisos de cada uno: las plantillas de consentimiento (Documentos y Firma, con
 * el bloque Firma eIDAS), las reglas de reserva (Disponibilidad) y las políticas de cobro (Pagos), estas dos en solo
 * lectura. Lo que no se puede leer se queda vacío sin romper la pantalla.
 */
export interface DatosServicios {
    servicios: ServicioCatalogo[];
    profesionales: Profesional[];
    plantillas: PlantillaDocumento[];
    reglas: ReglaReserva[];
    politicas: Politica[];
    /** Bloque Firma eIDAS contratado (sin él, el consentimiento se ve bloqueado). */
    firma: boolean;
    /**
     * Profesionales activos que caben en el plan (G481, G482): la cantidad de `profesional` (la base trae 1) más la de
     * `profesional_extra`. Null si no se pudieron leer los bloques.
     */
    limite: number | null;
    /** Cuántos `profesional_extra` hay contratados. */
    extras: number;
    moneda: string;
}

export function useDatosServicios(ops: Operaciones, negocio: string, puede: (codigo: string) => boolean) {
    const [datos, setDatos] = useState<DatosServicios | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const verPlantillas = puede('documentos.plantillas.gestionar');
    const verReglas = puede('disponibilidad.horarios.ver');
    const verPoliticas = puede('pagos.politicas.gestionar');

    useEffect(() => {
        let vivo = true;
        Promise.all([
            todas((cursor) => ops.listServices(negocio, { cursor, limit: 100 })),
            todas((cursor) => ops.listProfessionals(negocio, { cursor, limit: 100 })),
            ops.listEntitlements(negocio).catch(() => null),
            ops.getBusinessConVersion(negocio).catch(() => null),
        ])
            .then(async ([servicios, profesionales, bloques, n]) => {
                const lista = bloques?.entitlements ?? [];
                const firma = lista.some((e) => e.product_code === 'firma_eidas');
                const cantidad = (codigo: string) => lista.filter((e) => e.product_code === codigo).reduce((n, e) => n + e.quantity, 0);
                const limite = bloques === null ? null : cantidad('profesional') + cantidad('profesional_extra');
                const [plantillas, reglas, politicas] = await Promise.all([
                    firma && verPlantillas ? todas((cursor) => ops.listDocumentTemplates(negocio, { cursor, limit: 100 })).catch(() => []) : Promise.resolve([]),
                    verReglas ? todas((cursor) => ops.listBookingRules(negocio, { cursor, limit: 100 })).catch(() => []) : Promise.resolve([]),
                    verPoliticas ? todas((cursor) => ops.listPaymentPolicies(negocio, { cursor, limit: 100 })).catch(() => []) : Promise.resolve([]),
                ]);
                if (vivo) {
                    setDatos({ servicios, profesionales, plantillas, reglas, politicas, firma, limite, extras: cantidad('profesional_extra'), moneda: n?.datos.currency ?? 'EUR' });
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, verPlantillas, verReglas, verPoliticas]);

    /** Pone en la lista el servicio que devolvió la API (alta o cambio), sin volver a pedirlo todo. */
    const ponerServicio = useCallback((s: ServicioCatalogo) => {
        setDatos((d) => (d === null ? d : { ...d, servicios: d.servicios.some((x) => x.id === s.id) ? d.servicios.map((x) => (x.id === s.id ? s : x)) : [s, ...d.servicios] }));
    }, []);

    /** Pone en la lista el profesional que devolvió la API (alta o cambio). */
    const ponerProfesional = useCallback((p: Profesional) => {
        setDatos((d) => (d === null ? d : { ...d, profesionales: d.profesionales.some((x) => x.id === p.id) ? d.profesionales.map((x) => (x.id === p.id ? p : x)) : [...d.profesionales, p] }));
    }, []);

    /** Pone en la lista la plantilla que devolvió la API (alta, versión nueva o archivo). */
    const ponerPlantilla = useCallback((t: PlantillaDocumento) => {
        setDatos((d) => (d === null ? d : { ...d, plantillas: d.plantillas.some((x) => x.id === t.id) ? d.plantillas.map((x) => (x.id === t.id ? t : x)) : [t, ...d.plantillas] }));
    }, []);

    return { datos, error, reintentar: () => { setIntento((i) => i + 1); }, ponerServicio, ponerProfesional, ponerPlantilla };
}
